/* ========== PIE & DOUGHNUT CHARTS ==========
 * Subtypes:
 *  - Pie (drawPieChart, is3D=false)
 *  - 3-D Pie (drawPieChart, is3D=true)
 *  - Pie of Pie (drawPieOfPieChart, isBarOfPie=false)
 *  - Bar of Pie (drawPieOfPieChart, isBarOfPie=true)
 *  - Doughnut (drawPieChart, chartType='doughnut')
 *  - Exploded Pie (TODO — render with offset slices)
 *  - Exploded Doughnut (TODO — render with offset slices)
 * ============================================ */

import { COLORS, shadeColor } from './utils';

/**
 * Compose a slice's data-label string from the chart's authored dLbls
 * flags. Returns '' when no labels were enabled in the file — we must
 * not invent percentages/values Excel never showed.
 */
function buildSliceLabel(dataLabels, cat, value, total) {
  if (!dataLabels) return '';
  var parts = [];
  if (dataLabels.showCatName && cat) parts.push(cat);
  if (dataLabels.showVal) parts.push(value.toLocaleString());
  if (dataLabels.showPercent && total > 0) {
    parts.push(Math.round((Math.abs(value) / total) * 100) + '%');
  }
  return parts.join(', ');
}

/** Draw a label at the centroid of a pie/doughnut slice. */
function drawSliceLabel(ctx, label, cx, cy, rIn, rOut, a0, a1) {
  if (!label) return;
  var mid = (a0 + a1) / 2;
  var lr = rIn > 0 ? (rIn + rOut) / 2 : rOut * 0.62;
  if ((a1 - a0) < 0.18) return; // slice too thin to label
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 9px Calibri, Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, cx + Math.cos(mid) * lr, cy + Math.sin(mid) * lr);
}

export function drawPieChart(ctx, centerX, centerY, radius, chartObj, is3D) {
  var allSeries = chartObj && chartObj.series ? chartObj.series : [];
  if (allSeries.length === 0) return;
  var cats = chartObj.categories || [];
  var isDoughnut = chartObj.chartType === 'doughnut';

  // Filter to non-empty series. Excel writes "placeholder" series with all
  // zeros (Doughnut 1 has a Days ser of all 0s plus a Sales ser with the
  // actual numbers) — those shouldn't render. Without this filter we'd
  // return early with a blank chart.
  var rings = allSeries.filter(function (s) {
    if (!s.data || s.data.length === 0) return false;
    var t = s.data.reduce(function (a, v) { return a + Math.abs(v); }, 0);
    return t > 0;
  });
  if (rings.length === 0) return;

  // For pie (non-doughnut) only the first non-empty series renders, since
  // a pie has only one ring. Multi-series doughnuts render concentrically.
  var ringSpecs = isDoughnut ? rings : [rings[0]];

  // Allocate radii: each ring gets an equal slice of the band [innerR..radius].
  // innerR = 50% of radius for single ring (classic doughnut hole), or
  // 45% scaled inward as rings stack for multi-ring.
  var innerR = isDoughnut ? radius * 0.45 : 0;
  var ringBandW = (radius - innerR) / ringSpecs.length;

  // ── 3-D pie path (only valid for single-ring non-doughnut) ──────────────
  if (is3D && !isDoughnut) {
    var seriesData = ringSpecs[0].data;
    var pointColors = ringSpecs[0].pointColors || [];
    var total = seriesData.reduce(function (a, v) { return a + Math.abs(v); }, 0);
    function colorAt0(i) { return pointColors[i] || COLORS[i % COLORS.length]; }

    var depth = Math.min(18, radius * 0.18);
    var rx = radius;
    var ry = radius * 0.62;

    var shadowAngle = -Math.PI / 2;
    seriesData.forEach(function (value, index) {
      var sliceAngle = (Math.abs(value) / total) * Math.PI * 2;
      ctx.fillStyle = shadeColor(colorAt0(index), -25);
      ctx.beginPath();
      ctx.moveTo(centerX + Math.cos(shadowAngle) * rx, centerY + Math.sin(shadowAngle) * ry);
      ctx.ellipse(centerX, centerY, rx, ry, 0, shadowAngle, shadowAngle + sliceAngle);
      ctx.lineTo(centerX + Math.cos(shadowAngle + sliceAngle) * rx, centerY + Math.sin(shadowAngle + sliceAngle) * ry + depth);
      ctx.ellipse(centerX, centerY + depth, rx, ry, 0, shadowAngle + sliceAngle, shadowAngle, true);
      ctx.closePath();
      ctx.fill();
      shadowAngle += sliceAngle;
    });

    var topAngle = -Math.PI / 2;
    seriesData.forEach(function (value, index) {
      var sliceAngle = (Math.abs(value) / total) * Math.PI * 2;
      ctx.fillStyle = colorAt0(index);
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.ellipse(centerX, centerY, rx, ry, 0, topAngle, topAngle + sliceAngle);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // Authored data labels only
      var lbl3D = buildSliceLabel(chartObj.dataLabels, cats[index], value, total);
      if (lbl3D && sliceAngle > 0.18) {
        var midA = topAngle + sliceAngle / 2;
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 9px Calibri, Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(lbl3D, centerX + Math.cos(midA) * rx * 0.62, centerY + Math.sin(midA) * ry * 0.62);
      }
      topAngle += sliceAngle;
    });
  } else {
    // ── 2-D path: render each ring band as its own annulus ───────────────
    ringSpecs.forEach(function (ring, ringIdx) {
      var ringOuter = innerR + (ringIdx + 1) * ringBandW;
      var ringInner = innerR + ringIdx * ringBandW;
      var data = ring.data;
      var pointColors = ring.pointColors || [];
      var explosions = ring.pointExplosions || [];
      var ringTotal = data.reduce(function (a, v) { return a + Math.abs(v); }, 0);
      if (ringTotal === 0) return;

      var currentAngle = -Math.PI / 2;
      data.forEach(function (value, index) {
        var sliceAngle = (Math.abs(value) / ringTotal) * Math.PI * 2;

        // Exploded slices: offset the slice center along its mid-angle by
        // (explosion / 100) × radius (Excel's encoding is 0-400, where
        // 25 = 25% radius).
        var explosion = explosions[index] || 0;
        var sliceCx = centerX, sliceCy = centerY;
        if (explosion > 0) {
          var midAngle = currentAngle + sliceAngle / 2;
          var offset = (explosion / 100) * ringOuter * 0.5;
          sliceCx = centerX + Math.cos(midAngle) * offset;
          sliceCy = centerY + Math.sin(midAngle) * offset;
        }

        ctx.fillStyle = pointColors[index] || COLORS[index % COLORS.length];
        ctx.beginPath();
        if (isDoughnut || ringInner > 0) {
          ctx.arc(sliceCx, sliceCy, ringOuter, currentAngle, currentAngle + sliceAngle);
          ctx.arc(sliceCx, sliceCy, ringInner, currentAngle + sliceAngle, currentAngle, true);
          ctx.closePath();
        } else {
          ctx.moveTo(sliceCx, sliceCy);
          ctx.arc(sliceCx, sliceCy, ringOuter, currentAngle, currentAngle + sliceAngle);
          ctx.closePath();
        }
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
        // Authored data labels — only for the outermost ring to avoid clutter
        if (ringIdx === ringSpecs.length - 1) {
          var lbl = buildSliceLabel(chartObj.dataLabels, cats[index], value, ringTotal);
          drawSliceLabel(ctx, lbl, sliceCx, sliceCy, ringInner, ringOuter, currentAngle, currentAngle + sliceAngle);
        }
        currentAngle += sliceAngle;
      });
    });
  }

  // ── Legend ──────────────────────────────────────────────────────────────
  // Wrap onto multiple rows when labels overflow horizontally. Width to wrap
  // against is the pie's bounding box (2 × radius + margin).
  ctx.font = '9px Calibri, Arial';
  // Use the OUTERMOST ring's categories for legend (categories are shared
  // across rings, but we read from the first valid series's data length).
  var legendItems = [];
  var legendSeries = ringSpecs[0];
  var legendPointColors = legendSeries.pointColors || [];
  legendSeries.data.forEach(function (_, i) {
    var label = cats[i] || ('' + (i + 1));
    legendItems.push({
      idx: i,
      label: label,
      width: 14 + ctx.measureText(label).width + 16,
      color: legendPointColors[i] || COLORS[i % COLORS.length],
    });
  });
  // Wrap against the chart canvas width, not the pie's bounding box.
  // The pie is centered (centerX = canvasWidth / 2), so canvasWidth ≈ 2*centerX.
  // Using radius * 2.4 was way too tight whenever the pie was height-limited —
  // a small radius would force every label onto its own row even though the
  // chart canvas itself was plenty wide.
  var legendMaxW = Math.max(radius * 2.4, centerX * 1.85);
  var legendRows = wrapLegendRows(legendItems, legendMaxW);
  var rowH = 14;
  var legendStartY = centerY + (is3D ? radius * 0.62 : radius) + 18;
  ctx.textBaseline = 'middle';
  legendRows.forEach(function (row, rowI) {
    var rowWidth = row.reduce(function (s, it) { return s + it.width; }, 0);
    var rowX = centerX - rowWidth / 2;
    var rowY = legendStartY + rowI * rowH;
    row.forEach(function (item) {
      ctx.fillStyle = item.color;
      ctx.fillRect(rowX, rowY - 5, 10, 10);
      ctx.fillStyle = '#333';
      ctx.textAlign = 'left';
      ctx.fillText(item.label, rowX + 14, rowY);
      rowX += item.width;
    });
  });
}

export function drawPieOfPieChart(ctx, x, y, width, height, chartObj, isBarOfPie) {
  var series0 = chartObj && chartObj.series && chartObj.series[0] ? chartObj.series[0] : null;
  if (!series0 || !series0.data || series0.data.length === 0) return;
  var seriesData = series0.data;
  var pointColors = series0.pointColors || [];
  var cats = chartObj.categories || [];

  // ── Split data ──────────────────────────────────────────────────────────
  // Excel default is "last N values to secondary pie" with N=2 when no
  // <c:splitPos> is authored. ofPieGeom is read from the XML earlier.
  var geom = chartObj.ofPieGeom || { gapWidth: 100, secondPieSize: 75, splitType: 'auto', splitPos: 2 };
  var splitCount = Math.max(1, Math.min(seriesData.length - 1, Math.round(geom.splitPos || 2)));
  var splitIdx = seriesData.length - splitCount;
  var primaryData = seriesData.slice(0, splitIdx);
  var secondaryData = seriesData.slice(splitIdx);
  var secondaryTotal = secondaryData.reduce(function (s, v) { return s + Math.abs(v); }, 0);

  // KEY: Excel draws the aggregate "Other" slice FIRST in the primary pie
  // (starts at 12 o'clock, occupies the right side of the pie). The actual
  // data values follow it in their original order, going clockwise around
  // the rest of the pie. This is what makes the connector lines flow
  // naturally from the right side of the primary into the secondary pie.
  var primarySlices = [{ value: secondaryTotal, isAggregate: true }];
  primaryData.forEach(function (v, i) {
    primarySlices.push({ value: v, isAggregate: false, originalIdx: i });
  });
  var primaryTotal = primarySlices.reduce(function (s, sl) { return s + Math.abs(sl.value); }, 0);
  if (primaryTotal === 0) return;

  // ── Sizing ──────────────────────────────────────────────────────────────
  // secondPieSize is a % of the primary pie's diameter (Excel default 75%).
  // gapWidth (per OOXML) is the gap expressed as a % of secondary pie size;
  // so gapWidth=100 → gap = secondary radius (matches Excel's visual).
  //
  // Critical: compute the primary+gap+secondary block as a UNIT and center
  // it horizontally. The old code anchored primary to the left edge and
  // secondary to the right edge, so when pies were limited by height the
  // gap stretched across the entire chart width.
  //
  // We also need to count legend rows to know how much height to reserve.
  var secScale = (geom.secondPieSize || 75) / 100;
  var gapFactor = (geom.gapWidth || 100) / 100;

  // Pre-compute legend layout so we know how much vertical band it eats.
  ctx.font = '9px Calibri, Arial';
  var legendItems = [];
  for (var liA = 0; liA < seriesData.length; liA++) {
    var lblA = cats[liA] || ('' + (liA + 1));
    legendItems.push({ idx: liA, label: lblA, width: 14 + ctx.measureText(lblA).width + 16 });
  }
  var legendRows = wrapLegendRows(legendItems, width);
  var rowH = 14;
  var legendBand = legendRows.length * rowH + 8;
  var plotH = Math.max(60, height - legendBand);

  // Size pies so the full block fits in width AND height.
  // Block width = 2*pieR + gap + 2*secR
  //            = 2*pieR + (gapFactor * secR) + 2*secR
  //            = 2*pieR * (1 + secScale*(gapFactor/2 + 1))
  var widthFactor = 2 * (1 + secScale * (gapFactor / 2 + 1));
  var pieR = Math.min(width / widthFactor, plotH * 0.46);
  var secR = pieR * secScale;
  var gap = secR * gapFactor;
  var totalBlockW = 2 * pieR + gap + 2 * secR;
  var blockStartX = x + (width - totalBlockW) / 2;

  var pieCx = blockStartX + pieR;
  var pieCy = y + plotH / 2;
  var secCx = blockStartX + 2 * pieR + gap + secR;
  var secCy = y + plotH / 2;

  // ── Color helper ────────────────────────────────────────────────────────
  // The aggregate slice's color comes from pointColors[seriesData.length]
  // (Excel writes a dPt at idx=N for the aggregate, typically accent1 +
  // lumMod 60% = a darker shade of accent1).
  function colorForData(idx) {
    return pointColors[idx] || COLORS[idx % COLORS.length];
  }
  function colorForAggregate() {
    return pointColors[seriesData.length] || COLORS[0];
  }

  // ── Primary pie: aggregate FIRST, then data points clockwise ────────────
  var currentAngle = -Math.PI / 2;
  var aggStartAngle = currentAngle;
  var aggEndAngle = currentAngle;
  primarySlices.forEach(function (slice) {
    var sliceAngle = (Math.abs(slice.value) / primaryTotal) * Math.PI * 2;
    ctx.fillStyle = slice.isAggregate ? colorForAggregate() : colorForData(slice.originalIdx);
    ctx.beginPath();
    ctx.moveTo(pieCx, pieCy);
    ctx.arc(pieCx, pieCy, pieR, currentAngle, currentAngle + sliceAngle);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    if (slice.isAggregate) {
      aggStartAngle = currentAngle;
      aggEndAngle = currentAngle + sliceAngle;
    }
    currentAngle += sliceAngle;
  });

  // ── Secondary plot ──────────────────────────────────────────────────────
  if (isBarOfPie) {
    var barW = secR * 1.4;
    var barH = secR * 2;
    var barX = secCx - barW / 2;
    var barY = secCy - barH / 2;
    if (secondaryTotal > 0) {
      var stackedY = barY;
      secondaryData.forEach(function (val, i) {
        var sh = (Math.abs(val) / secondaryTotal) * barH;
        ctx.fillStyle = colorForData(splitIdx + i);
        ctx.fillRect(barX, stackedY, barW, sh);
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.strokeRect(barX, stackedY, barW, sh);
        stackedY += sh;
      });
    }

    // Leader lines: top of aggregate → top of bar, bottom of aggregate → bottom of bar
    var laStartX = pieCx + Math.cos(aggStartAngle) * pieR;
    var laStartY = pieCy + Math.sin(aggStartAngle) * pieR;
    var laEndX = pieCx + Math.cos(aggEndAngle) * pieR;
    var laEndY = pieCy + Math.sin(aggEndAngle) * pieR;
    ctx.strokeStyle = '#A6A6A6';
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(laStartX, laStartY);
    ctx.lineTo(barX, barY);
    ctx.moveTo(laEndX, laEndY);
    ctx.lineTo(barX, barY + barH);
    ctx.stroke();
  } else {
    // Secondary pie — drawn COUNTER-CLOCKWISE from 12 o'clock so the first
    // secondary value lands on the LEFT half (adjacent to the connector
    // lines coming from the primary's right side), matching Excel.
    if (secondaryTotal > 0) {
      var ang2 = -Math.PI / 2;
      secondaryData.forEach(function (val, i) {
        var sliceA = (Math.abs(val) / secondaryTotal) * Math.PI * 2;
        ctx.fillStyle = colorForData(splitIdx + i);
        ctx.beginPath();
        ctx.moveTo(secCx, secCy);
        // anticlockwise=true: arc sweeps counter-clockwise from ang2 to ang2-sliceA
        ctx.arc(secCx, secCy, secR, ang2, ang2 - sliceA, true);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ang2 -= sliceA;
      });
    }

    // Leader lines from the aggregate slice's two edges to the secondary
    // pie's top and bottom. Always SOLID (Excel uses <c:serLines> with
    // cmpd="sng", a single solid line).
    var lpStartX = pieCx + Math.cos(aggStartAngle) * pieR;
    var lpStartY = pieCy + Math.sin(aggStartAngle) * pieR;
    var lpEndX = pieCx + Math.cos(aggEndAngle) * pieR;
    var lpEndY = pieCy + Math.sin(aggEndAngle) * pieR;
    ctx.strokeStyle = '#A6A6A6';
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(lpStartX, lpStartY);
    ctx.lineTo(secCx, secCy - secR);    // top of secondary
    ctx.moveTo(lpEndX, lpEndY);
    ctx.lineTo(secCx, secCy + secR);    // bottom of secondary
    ctx.stroke();
  }

  // ── Legend underneath ───────────────────────────────────────────────────
  // legendRows was computed up top so legendBand reserved enough height.
  ctx.font = '9px Calibri, Arial';
  ctx.textBaseline = 'middle';
  var rowYStart = y + height - legendBand + rowH / 2 + 2;
  legendRows.forEach(function (row, rowI) {
    var rowWidth = row.reduce(function (s, it) { return s + it.width; }, 0);
    var rowX = x + (width - rowWidth) / 2;
    var rowY = rowYStart + rowI * rowH;
    row.forEach(function (item) {
      ctx.fillStyle = colorForData(item.idx);
      ctx.fillRect(rowX, rowY - 5, 10, 10);
      ctx.fillStyle = '#333';
      ctx.textAlign = 'left';
      ctx.fillText(item.label, rowX + 14, rowY);
      rowX += item.width;
    });
  });
}

/**
 * Pack legend items into rows so no row exceeds `maxWidth`.
 * Returns [[item, item, ...], [item, ...], ...].
 */
function wrapLegendRows(items, maxWidth) {
  if (items.length === 0) return [];
  var rows = [];
  var current = [];
  var currentW = 0;
  for (var i = 0; i < items.length; i++) {
    var it = items[i];
    if (current.length > 0 && currentW + it.width > maxWidth) {
      rows.push(current);
      current = [];
      currentW = 0;
    }
    current.push(it);
    currentW += it.width;
  }
  if (current.length > 0) rows.push(current);
  return rows;
}
